import React, { useEffect, useMemo, useRef, useState } from "react";
import EditCartModal from "../elements/EditCartModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-svg-core/styles.css";

const THEME_COLOR = "#9d2a00"; // 🔸 your brand color

export default function CartDrawer({
  open,
  onClose,
  restaurantId,
  changedCart,
  setChangedCart,
  language,
}) {
  const overlayRef = useRef(null);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editCartModal, setEditCartModal] = useState(false);

  const refresh = () => {
    try {
      const storedCart = JSON.parse(localStorage.getItem(`cart${restaurantId}`) || "[]");
      const merged = Array.isArray(storedCart)
        ? storedCart.map((it, idx) => ({ ...it, __index: idx }))
        : [];
      setItems(merged);
    } catch {
      setItems([]);
    }
  };

  // Lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  useEffect(refresh, [changedCart, restaurantId]);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it?.price) || 0), 0),
    [items]
  );

  const updateLocalStorageCart = (index, change) => {
    const key = `cart${restaurantId}`;
    const current = JSON.parse(localStorage.getItem(key) || "[]");
    if (!current[index]) return;

    const next = [...current];
    const prevQty = Number(next[index].quantity) || 1;

    if (change === -1 && prevQty <= 1) {
      next.splice(index, 1);
    } else {
      const qty = prevQty + change;
      next[index].quantity = qty;

      const base = Number(next[index]?.Item?.price) || 0;
      const modifiers = Array.isArray(next[index]?.options)
        ? next[index].options.reduce(
            (sum, opt) => sum + (Number(opt?.priceModifier) || 0),
            0
          )
        : 0;

      next[index].price = Number(((base + modifiers) * qty).toFixed(2));
    }

    localStorage.setItem(key, JSON.stringify(next));
    refresh();
    setChangedCart((v) => !v);
  };

  const handleRowClick = (it) => {
    const base = {
      id: it?.Item?.id,
      name: it?.Item?.name,
      price: it?.Item?.price ?? it?.price,
      quantity: it?.quantity ?? 1,
      special_instructions: it?.special_instructions,
      image_url: it?.Item?.image_url,
      Restaurant_Item_Attributes: it?.Item?.Restaurant_Item_Attributes,
      options: it?.options,
      category_id: it?.Item?.category_id,
      index: it?.__index,
    };
    setSelectedItem(base);
    setEditCartModal(true);
  };

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        onClick={(e) => e.target === overlayRef.current && onClose?.()}
        onTouchMove={(e) => e.preventDefault()}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.45)",
          backdropFilter: "blur(2px)",
          zIndex: 100,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      />

      {/* Mobile Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        className="d-md-none position-fixed shadow"
        style={{
          zIndex: 110,
          backgroundColor: "#fff",
          left: 0,
          right: 0,
          bottom: 0,
          height: "85vh",
          borderTopLeftRadius: "1.25rem",
          borderTopRightRadius: "1.25rem",
          transform: open ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DrawerHeader onClose={onClose} />
        <DrawerContent
          items={items}
          onRowClick={handleRowClick}
          onInc={(i) => updateLocalStorageCart(i, +1)}
          onDec={(i) => updateLocalStorageCart(i, -1)}
          language={language}
        />
        <DrawerFooter subtotal={subtotal} />
      </div>

      {/* Desktop Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        className="d-none d-md-flex position-fixed shadow"
        style={{
          zIndex: 110,
          backgroundColor: "#fff",
          top: "1rem",
          right: "1rem",
          bottom: "1rem",
          width: "100%",
          maxWidth: 420,
          borderRadius: "1.25rem",
          transform: open ? "translateX(0)" : "translateX(120%)",
          transition: "transform 0.3s ease",
          flexDirection: "column",
        }}
      >
        <DrawerHeader onClose={onClose} />
        <DrawerContent
          items={items}
          onRowClick={handleRowClick}
          onInc={(i) => updateLocalStorageCart(i, +1)}
          onDec={(i) => updateLocalStorageCart(i, -1)}
          language={language}
        />
        <DrawerFooter subtotal={subtotal} />
      </div>

      <EditCartModal modal={editCartModal} item={selectedItem} setModal={setEditCartModal} />
    </>
  );
}

/* -------------------- Header -------------------- */
function DrawerHeader({ onClose }) {
  return (
    <header
      className="px-3 py-2 d-flex align-items-center justify-content-between text-white"
      style={{
        backgroundColor: THEME_COLOR,
        borderTopLeftRadius: "1.25rem",
        borderTopRightRadius: "1.25rem",
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <FontAwesomeIcon icon={faShoppingCart} style={{ fontSize: 14, color: "#fff" }} />
        <h6 className="m-0 fw-semibold text-white">Your Cart</h6>
      </div>
      <button
        onClick={onClose}
        className="btn btn-light d-inline-flex align-items-center justify-content-center"
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          backgroundColor: "#fff",
          color: THEME_COLOR,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </header>
  );
}

/* -------------------- Content -------------------- */
function DrawerContent({ items, onRowClick, onInc, onDec, language }) {
  return (
    <div className="flex-grow-1 overflow-auto px-3 py-2" style={{ overscrollBehavior: "contain" }}>
      {items.length === 0 ? (
        <div className="d-grid text-muted text-center" style={{ height: 80, placeItems: "center" }}>
          Your cart is empty.
        </div>
      ) : (
        items.map((it, idx) => {
          const priceLabel = `$${Number(it?.price || 0).toFixed(2)}`;
          return (
            <div
              key={`${it?.Item?.id || idx}-${it?.quantity || 1}-${idx}`}
              className="border rounded-3 p-2 mb-2 bg-white"
              style={{ minHeight: 72, cursor: "pointer", borderColor: "#f0f0f0" }}
              onClick={() => onRowClick(it)}
            >
              <div className="d-flex align-items-center gap-2">
                <img
                  src={it?.Item?.image_url || "/foodph.png"}
                  alt={it?.Item?.name || "item image"}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold text-black text-truncate">
                    {language === "en"
                      ? it?.Item?.name
                      : it?.Item?.chinese_name?.trim() || it?.Item?.name}
                  </div>

                  {Array.isArray(it?.options) && it.options.length > 0 && (
                    <div className="small text-muted text-truncate">
                      <span className="text-black fw-medium">Options:</span>{" "}
                      {it.options
                        .map((o) => {
                          const mod =
                            (o?.priceModifier || 0) > 0
                              ? ` +$${o.priceModifier}`
                              : "";
                          return `${o?.name || "Option"}${mod}`;
                        })
                        .join(" • ")}
                    </div>
                  )}

                  {!!it?.special_instructions && (
                    <div className="small text-muted text-truncate">
                      <span className="text-black fw-medium">Notes:</span>{" "}
                      {it.special_instructions}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer controls */}
              <div
                className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="fw-semibold text-black">{priceLabel}</div>
                <div
                  className="d-inline-flex align-items-center gap-2 px-2 py-1"
                  style={{
                    borderRadius: 999,
                    border: `1px solid ${THEME_COLOR}`,
                  }}
                >
                  <button
                    className="btn btn-sm text-white"
                    style={{
                      backgroundColor: THEME_COLOR,
                      borderRadius: 16,
                      width: 28,
                      height: 28,
                      padding: 0,
                    }}
                    onClick={() => onDec(it.__index)}
                  >
                    –
                  </button>
                  <span
                    className="text-black fw-semibold"
                    style={{ minWidth: "2ch", textAlign: "center" }}
                  >
                    {it?.quantity ?? 1}
                  </span>
                  <button
                    className="btn btn-sm text-white"
                    style={{
                      backgroundColor: THEME_COLOR,
                      borderRadius: 16,
                      width: 28,
                      height: 28,
                      padding: 0,
                    }}
                    onClick={() => onInc(it.__index)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* -------------------- Footer -------------------- */
function DrawerFooter({ subtotal }) {
  const navigate = useNavigate();
  const canCheckout = Number(subtotal || 0) > 0;

  return (
    <div
      className="border-top p-3 bg-white"
      style={{
        borderBottomLeftRadius: "1.25rem",
        borderBottomRightRadius: "1.25rem",
      }}
    >
      <div className="d-flex align-items-center justify-content-between text-dark fw-semibold">
        <span>Subtotal</span>
        <span className="fs-5 fw-bold text-black">
          ${Number(subtotal || 0).toFixed(2)}
        </span>
      </div>

      <button
        className="w-100 mt-3 text-white fw-semibold"
        disabled={!canCheckout}
        onClick={() => navigate("/checkout")}
        style={{
          backgroundColor: THEME_COLOR,
          border: "none",
          borderRadius: 20,
          padding: "0.75rem 0",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          opacity: canCheckout ? 1 : 0.6,
        }}
      >
        Go to Checkout
      </button>
    </div>
  );
}
